import React, { useEffect, useRef, useState } from "react";
import { KeyRound, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCrypto } from "../context/CryptoContext";
import { RecoveryPhrase } from "./CryptoGate";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 text-white font-body text-sm outline-none focus:border-[#14A89A] mb-2";

// Check-in: test the written-down phrase on-device; nothing is saved or sent.
const PhraseCheck = ({ onClose, onCreateNew }) => {
  const { checkRecoveryPhrase } = useCrypto();
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // "ok" | "nomatch" | "badpw"

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setResult(null);
    try {
      const r = await checkRecoveryPhrase(password, phrase);
      setResult(!r.passwordOk ? "badpw" : r.phraseOk ? "ok" : "nomatch");
      if (r.passwordOk) setPhrase("");
    } catch { setResult("nomatch"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="phrase-check-form">
      <p className="font-body text-xs text-zinc-400 mb-2">For peace of mind: type your 12 words. They're checked on this device only, never sent or saved.</p>
      <input type="password" autoComplete="current-password" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="phrase-check-password" className={inputCls} />
      <textarea rows={3} autoComplete="off" autoCapitalize="none" spellCheck={false} placeholder="Your 12-word recovery phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="phrase-check-input" className={inputCls} />
      {result === "ok" && <p className="flex items-center gap-1.5 text-[#14A89A] text-xs mb-2 font-body" data-testid="phrase-check-ok"><CheckCircle2 size={14} /> This phrase works. You're all set.</p>}
      {result === "nomatch" && (
        <p className="text-amber-300 text-xs mb-2 font-body leading-relaxed" data-testid="phrase-check-nomatch">
          <XCircle size={14} className="inline mr-1 -mt-0.5" />This phrase doesn't match. Check the word order and spelling, or{" "}
          <button type="button" onClick={onCreateNew} className="underline underline-offset-2" data-testid="phrase-check-create-new">create a new phrase</button>.
        </p>
      )}
      {result === "badpw" && <p className="text-red-400 text-xs mb-2 font-body" data-testid="phrase-check-badpw">That password isn't right.</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy || !password || phrase.trim().split(/\s+/).length < 12} data-testid="phrase-check-submit" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs disabled:opacity-40 flex items-center gap-1.5">
          {busy && <Loader2 size={13} className="animate-spin" />} Check phrase
        </button>
        <button type="button" onClick={onClose} data-testid="phrase-check-close" className="border border-white/15 text-zinc-400 px-3 py-2 font-body text-xs">Close</button>
      </div>
    </form>
  );
};

// Settings: replace a lost recovery phrase. Needs password (+ 2FA if on); old phrase stops working.
export const RecoveryPhraseSettings = () => {
  const { user } = useAuth();
  const { rotateRecoveryPhrase, status } = useCrypto();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [phrase, setPhrase] = useState(null);
  const [done, setDone] = useState(false);
  const [checking, setChecking] = useState(() => new URLSearchParams(window.location.search).get("checkphrase") === "1");
  const boxRef = useRef(null);
  const mfa = !!user?.mfa_enabled;
  useEffect(() => { if (checking) boxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [checking]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      setPhrase(await rotateRecoveryPhrase(password, code));
      setOpen(false); setPassword(""); setCode("");
    } catch (err) { setError(err?.response?.data?.detail || err?.message || "Couldn't create a new phrase."); }
    finally { setBusy(false); }
  };

  if (phrase) return <RecoveryPhrase phrase={phrase} onDone={() => { setPhrase(null); setDone(true); }} />;

  return (
    <div ref={boxRef} className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="recovery-phrase-settings">
      <div className="flex items-center gap-2 mb-1"><KeyRound size={16} className="text-[#14A89A]" /><span className="font-body text-sm text-white">Recovery phrase</span></div>
      <p className="font-body text-xs text-zinc-500 mb-3 leading-relaxed">
        Check that your written-down words still work, or create a new set if they're lost. A new set replaces the old one immediately, and we'll email you to confirm. We never see your words.
      </p>
      {done && <p className="font-body text-xs text-[#14A89A] mb-3" data-testid="recovery-rotated-note">New recovery phrase saved. Your old one no longer works.</p>}
      {checking ? (
        <PhraseCheck onClose={() => setChecking(false)} onCreateNew={() => { setChecking(false); setOpen(true); }} />
      ) : !open ? (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setChecking(true); setDone(false); }} disabled={status !== "unlocked"} data-testid="phrase-check-open" className="border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:bg-white/5 disabled:opacity-40">
            Check my phrase
          </button>
          <button onClick={() => { setOpen(true); setError(""); setDone(false); }} disabled={status !== "unlocked"} data-testid="recovery-rotate-open" className="border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:bg-white/5 disabled:opacity-40">
            Create a new recovery phrase
          </button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <input type="password" autoComplete="current-password" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="recovery-rotate-password" className={inputCls} />
          {mfa && <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="Two-factor code or backup code" data-testid="recovery-rotate-mfa" className={inputCls} />}
          {error && <p className="text-red-400 text-xs mb-2 font-body" data-testid="recovery-rotate-error">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !password || (mfa && !code)} data-testid="recovery-rotate-submit" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs disabled:opacity-40 flex items-center gap-1.5">
              {busy && <Loader2 size={13} className="animate-spin" />} Create new phrase
            </button>
            <button type="button" onClick={() => setOpen(false)} data-testid="recovery-rotate-cancel" className="border border-white/15 text-zinc-400 px-3 py-2 font-body text-xs">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};
